import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { F0Box, F0Dialog } from '@factorialco/f0-react'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  PurchaseOrderDetailDocument,
  type PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import PurchaseOrderDetail from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail'
import SidepanelDialogTitle from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/SidepanelDialogTitle'
import { useFooterActions } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/TemplateSidepanelFooter/useFooterActions'
import { useEditPurchaseOrder } from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/useEditPurchaseOrder'
import {
  replaceLastPathSegment,
  usePurchaseOrderListNavigation,
} from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetail/usePurchaseOrderListNavigation'
import Footer from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetailPanel/Footer'
import PurchaseOrderDetailPanel from 'modules/procurement/components/purchaseOrders/PurchaseOrderDetailPanel'
import type { PurchaseOrdersOutletContext } from 'modules/procurement/pages/PurchaseOrdersPage'

// Stable empty fallback — a fresh `[]` each render would bust the nav hook's memoization.
const EMPTY_V2_IDS: readonly string[] = []

interface V2SidepanelProps {
  purchaseOrder: PurchaseOrderDetailFieldsFragment
  canManagePurchaseOrder: boolean
  onClose: () => void
  navigation?: ReturnType<typeof usePurchaseOrderListNavigation>
}

function V2Sidepanel({
  purchaseOrder,
  canManagePurchaseOrder,
  onClose,
  navigation: navigationProps,
}: V2SidepanelProps) {
  const edit = useEditPurchaseOrder(purchaseOrder)
  const [currentTab, setCurrentTab] = useState('')
  const resolvedTab = currentTab || 'details'

  const showFooter = canManagePurchaseOrder && resolvedTab !== 'attachments'

  const canEdit =
    canManagePurchaseOrder &&
    !edit.isEditing &&
    purchaseOrder.status !== 'processing' &&
    purchaseOrder.status !== 'closed'

  const footerActions = useFooterActions({
    purchaseOrder,
    editMode: edit.editMode,
    cancelEditing: edit.cancelEditing,
    onReviewAndCreate: () => edit.startEditing('create'),
    formRef: edit.formRef,
  })

  // When creating from draft, transition to pending; otherwise save in place.
  const targetStatus = edit.editMode === 'create' ? 'pending' : undefined

  const editAction = canEdit
    ? [
        {
          label: i18n.t('common.edit'),
          icon: Pencil,
          onClick: () => edit.startEditing('edit'),
        },
      ]
    : []

  return (
    <F0Dialog
      position="right"
      width="xl"
      isOpen
      onClose={onClose}
      title={<SidepanelDialogTitle navigation={navigationProps} hideNavigation={edit.isEditing} />}
      otherActions={editAction.length > 0 ? editAction : undefined}
      primaryAction={showFooter ? footerActions.primaryAction : undefined}
      secondaryAction={showFooter ? footerActions.secondaryAction : undefined}
    >
      <PurchaseOrderDetail
        purchaseOrder={purchaseOrder}
        currentTab={resolvedTab}
        onTabChange={setCurrentTab}
        isEditing={edit.isEditing}
        formRef={edit.formRef}
        lineItemsRef={edit.lineItemsRef}
        lineItems={edit.lineItems}
        editCallbacks={{
          replaceLineItems: edit.replaceLineItems,
          setLineItemFieldValue: edit.setLineItemFieldValue,
          addLineItem: edit.addLineItem,
          removeLineItem: edit.removeLineItem,
        }}
        onEditSuccess={edit.onEditSuccess}
        targetStatus={targetStatus}
      />
    </F0Dialog>
  )
}

const PurchaseOrderDetailPage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams()
  const { company } = useCurrentSession()
  const [currentTab, setCurrentTab] = useState('')

  // Prev/next navigation — use local state to avoid remounting the dialog
  const [activeId, setActiveId] = useState(id ?? '')
  // Reset when the route-level id changes (e.g., user clicks a different row)
  useEffect(() => {
    if (id) setActiveId(id)
  }, [id])

  const outletContext = useOutletContext<PurchaseOrdersOutletContext | null>()

  // Silently swap the active PO without triggering a React Router remount:
  // update local state + rewrite the final URL segment in place.
  const navigateToId = useCallback((nextId: string) => {
    setActiveId(nextId)
    const nextUrl = replaceLastPathSegment(new URL(window.location.href), nextId)
    window.history.replaceState(null, '', nextUrl.toString())
  }, [])

  const listNavigation = usePurchaseOrderListNavigation(
    activeId,
    outletContext?.visibleV2Ids ?? EMPTY_V2_IDS,
    navigateToId
  )

  const { data, error } = useQuery(PurchaseOrderDetailDocument, {
    variables: {
      companyId: company.id,
      id: activeId,
    },
    skip: !activeId,
  })

  const purchaseOrder = data?.procurement?.purchaseOrder
  const canManagePurchaseOrder = data?.procurement?._policies?.purchaseOrderManage.can ?? false
  const isV2 =
    (data?.features?.poTemplatesFeature?.edges?.length ?? 0) > 0 &&
    !!purchaseOrder?.poTemplateVersion

  if (!activeId) {
    navigate(navigation.procurement.purchaseOrders.fullPath())
    return null
  }

  const onClose = () => {
    navigate(navigation.procurement.purchaseOrders.fullPath())
  }

  if (error) {
    return (
      <Sidepanel
        width="wide"
        title={i18n.t('procurement.purchase_orders.detail.title')}
        onClose={onClose}
        open
        headerBorder
        closeLabel={i18n.t('common.close')}
        boxPadding={false}
      >
        <ErrorBoundary blankSlate />
      </Sidepanel>
    )
  }

  if (!data || !purchaseOrder) {
    return (
      <Sidepanel
        width="wide"
        title={i18n.t('procurement.purchase_orders.detail.title')}
        onClose={onClose}
        open
        headerBorder
        closeLabel={i18n.t('common.close')}
        boxPadding={false}
      >
        <F0Box display="flex" justifyContent="center" alignItems="center" grow>
          <Spinner size="large" />
        </F0Box>
      </Sidepanel>
    )
  }

  if (isV2) {
    return (
      <V2Sidepanel
        purchaseOrder={purchaseOrder}
        canManagePurchaseOrder={canManagePurchaseOrder}
        onClose={onClose}
        navigation={listNavigation}
      />
    )
  }

  const showFooter = canManagePurchaseOrder
  const v1Footer = showFooter ? <Footer purchaseOrder={purchaseOrder} /> : undefined

  return (
    <Sidepanel
      width="wide"
      title={i18n.t('procurement.purchase_orders.detail.title')}
      onClose={onClose}
      open
      headerBorder
      closeLabel={i18n.t('common.close')}
      boxPadding={false}
      footer={v1Footer}
    >
      <PurchaseOrderDetailPanel
        purchaseOrder={purchaseOrder}
        currentTab={currentTab || 'summary'}
        onTabChange={setCurrentTab}
      />
    </Sidepanel>
  )
}

export default PurchaseOrderDetailPage
